import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const SITE_NAV_LINKS = [
  { label: "Overview", path: "/" },
  { label: "Products", path: "/products" },
  { label: "Blogs", path: "/blogs" },
  { label: "Cookbooks", path: "/cookbooks" },
];

const PORTFOLIO_NAV_LINKS = [
  { label: "About", path: "/about" },
  { label: "Projects", path: "/projects" },
  { label: "Experience", path: "/experience" },
  { label: "Education", path: "/education" },
  { label: "Blogs", path: "/blogs" },
  { label: "Cookbooks", path: "/cookbooks" },
  { label: "Contact", path: "/contact" },
];

const isPortfolioHost =
  typeof window !== "undefined" &&
  window.location.hostname.startsWith("portfolio.");

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navLinks = isPortfolioHost ? PORTFOLIO_NAV_LINKS : SITE_NAV_LINKS;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (link) => location.pathname === link.path;

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-[9000] transition-colors duration-200 ${
          scrolled
            ? "bg-[#F5F5F0]/95 backdrop-blur-md border-b border-[#D4D4D4]"
            : "bg-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-2 group"
          >
            <img
              src="/logo.svg"
              alt="Melonai"
              className="w-9 h-9 transition-transform duration-150 group-hover:rotate-3"
            />
            <span className="font-pixel text-[#15803D] text-xs md:text-sm tracking-wider group-hover:neon-text transition-colors">
              MELONAI
            </span>
          </Link>

          {/* Desktop Nav - right-aligned */}
          <div className="hidden lg:flex items-center gap-1 ml-auto">
            {navLinks.map((link) => {
              const className = `font-arcade text-lg uppercase tracking-wider px-4 py-2 transition-colors duration-150 ${
                isActive(link)
                  ? "text-[#15803D] border-b-2 border-[#4ADE80]"
                  : "text-[#0A0A0A]/70 hover:text-[#15803D]"
              }`;
              if (link.external) {
                return (
                  <a
                    key={link.path}
                    href={link.path}
                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                    className={className}
                  >
                    {link.label}
                  </a>
                );
              }
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                  className={className}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="nav-hamburger"
            className="lg:hidden text-[#0A0A0A]/80 hover:text-[#15803D] transition-colors p-1"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>
      </header>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="fixed inset-0 z-[8999] bg-[#F5F5F0]/98 flex flex-col lg:hidden"
        >
          <div className="h-16 flex items-center justify-between px-4 border-b border-[#D4D4D4]">
            <div className="flex items-center gap-2">
              <img src="/logo.svg" alt="Melonai" className="w-8 h-8" />
              <span className="font-pixel text-[#15803D] text-sm">MELONAI</span>
            </div>
            <button
              onClick={() => setMenuOpen(false)}
              className="text-[#0A0A0A]/80 hover:text-[#15803D] transition-colors"
              aria-label="Close menu"
            >
              <X size={22} />
            </button>
          </div>
          <div className="flex-1 flex flex-col py-8 px-6 gap-1">
            {navLinks.map((link, i) => {
              const className = `font-arcade text-3xl uppercase tracking-wider py-4 border-b border-[#E5E5E5] animate-fadeInLeft opacity-0 [animation-fill-mode:forwards] ${
                isActive(link)
                  ? "text-[#15803D]"
                  : "text-[#0A0A0A]/70 hover:text-[#15803D]"
              }`;
              if (link.external) {
                return (
                  <a
                    key={link.path}
                    href={link.path}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    style={{ animationDelay: `${i * 60}ms` }}
                    className={className}
                  >
                    &gt; {link.label}
                  </a>
                );
              }
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  style={{ animationDelay: `${i * 60}ms` }}
                  className={className}
                >
                  &gt; {link.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
